import { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  Alert,
  ScrollView,
} from "react-native";
import { Link, useRouter } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Image } from "expo-image";

/**
 * Sign Up Screen Component
 */
export default function SignUpScreen() {
  const router = useRouter();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [familyPassword, setFamilyPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showFamilyPassword, setShowFamilyPassword] = useState(false);
  const { signUp, isLoading } = useAuth();
  const colors = useThemeColors();

  /**
   * Handle sign up form submission
   */
  const handleSignUp = async () => {
    if (!fullName || !email || !password || !familyPassword) {
      Alert.alert("Fout", "Vul alle velden in");
      return;
    }

    if (password.length < 6) {
      Alert.alert("Fout", "Wachtwoord moet minimaal 6 karakters zijn");
      return;
    }

    try {
      await signUp(email, password, fullName, familyPassword);
      // Redirect to email verification screen
      router.push({
        pathname: "/(auth)/verify",
        params: { email },
      });
    } catch (error: any) {
      Alert.alert(
        "Fout",
        error.message || "Er ging iets mis bij het registreren"
      );
    }
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        <ScrollView
          className="flex-1 px-6"
          contentContainerStyle={{ paddingVertical: 32 }}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View className="flex-row items-center justify-between mb-8">
            <Text className="text-2xl font-bold" style={{ color: colors.text }}>
              Staatsen App
            </Text>
            <View className="w-14 h-14 rounded-2xl overflow-hidden shadow-md">
              <Image
                source={require("@/assets/app_icon/ios-dark.png")}
                style={{ width: "100%", height: "100%" }}
                contentFit="cover"
              />
            </View>
          </View>

          {/* Form */}
          <View
            className="rounded-2xl p-6"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            <Text className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
              Maak jouw account
            </Text>
            <Text className="mb-6" style={{ color: colors.textSecondary }}>
              Word lid van de familie app
            </Text>

            {/* Full Name Input */}
            <View className="mb-4">
              <Text className="mb-2 font-medium" style={{ color: colors.text }}>
                Naam
              </Text>
              <View
                className="flex-row items-center rounded-xl px-4"
                style={{ backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="person-outline" size={20} color={colors.textTertiary} />
                <TextInput
                  className="flex-1 py-3 px-3"
                  style={{ color: colors.text }}
                  placeholder="Jouw naam"
                  placeholderTextColor={colors.textTertiary}
                  value={fullName}
                  onChangeText={setFullName}
                  autoComplete="name"
                />
              </View>
            </View>

            {/* Email Input */}
            <View className="mb-4">
              <Text className="mb-2 font-medium" style={{ color: colors.text }}>
                Email
              </Text>
              <View
                className="flex-row items-center rounded-xl px-4"
                style={{ backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="mail-outline" size={20} color={colors.textTertiary} />
                <TextInput
                  className="flex-1 py-3 px-3"
                  style={{ color: colors.text }}
                  placeholder="Jouw email"
                  placeholderTextColor={colors.textTertiary}
                  value={email}
                  onChangeText={setEmail}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  autoComplete="email"
                />
              </View>
            </View>

            {/* Password Input */}
            <View className="mb-4">
              <Text className="mb-2 font-medium" style={{ color: colors.text }}>
                Wachtwoord
              </Text>
              <View
                className="flex-row items-center rounded-xl px-4"
                style={{ backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="lock-closed-outline" size={20} color={colors.textTertiary} />
                <TextInput
                  className="flex-1 py-3 px-3"
                  style={{ color: colors.text }}
                  placeholder="Maak een wachtwoord aan"
                  placeholderTextColor={colors.textTertiary}
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  autoComplete="new-password"
                />
                <TouchableOpacity
                  onPress={() => setShowPassword(!showPassword)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  accessibilityRole="button"
                  accessibilityLabel={showPassword ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                >
                  <Ionicons
                    name={showPassword ? "eye-outline" : "eye-off-outline"}
                    size={20}
                    color={colors.textTertiary}
                  />
                </TouchableOpacity>
              </View>
            </View>

            {/* Family Password Input */}
            <View className="mb-6">
              <Text className="mb-2 font-medium" style={{ color: colors.text }}>
                🔒 Familie Wachtwoord
              </Text>
              <View
                className="flex-row items-center rounded-xl px-4"
                style={{ backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="key-outline" size={20} color={colors.textTertiary} />
                <TextInput
                  className="flex-1 py-3 px-3"
                  style={{ color: colors.text }}
                  placeholder="Familie wachtwoord"
                  placeholderTextColor={colors.textTertiary}
                  value={familyPassword}
                  onChangeText={setFamilyPassword}
                  secureTextEntry={!showFamilyPassword}
                />
                <TouchableOpacity
                  onPress={() => setShowFamilyPassword(!showFamilyPassword)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  accessibilityRole="button"
                  accessibilityLabel={showFamilyPassword ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                >
                  <Ionicons
                    name={showFamilyPassword ? "eye-outline" : "eye-off-outline"}
                    size={20}
                    color={colors.textTertiary}
                  />
                </TouchableOpacity>
              </View>
              <Text className="text-sm mt-2" style={{ color: colors.textTertiary }}>
                Vraag dit wachtwoord aan een familielid
              </Text>
            </View>

            {/* Sign Up Button */}
            <TouchableOpacity
              className="rounded-xl py-4 items-center"
              style={{
                backgroundColor: isLoading ? `${colors.primary}80` : colors.primary,
              }}
              onPress={handleSignUp}
              disabled={isLoading}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Aanmelden"
            >
              <Text className="text-white font-semibold text-lg">
                {isLoading ? "Laden..." : "Sign Up"}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Sign In Link */}
          <View className="flex-row justify-center mt-6">
            <Text style={{ color: colors.textSecondary }}>Heb je al een account? </Text>
            <Link href="/(auth)/login" asChild>
              <TouchableOpacity accessibilityRole="link">
                <Text className="font-semibold" style={{ color: colors.primary }}>
                  Sign In
                </Text>
              </TouchableOpacity>
            </Link>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
